import { memo } from "react";

import { Icon } from "@components/Icon";
import { useCarouselNavigationAria } from "@react/a11y/carouselAria";
import { type TTailwindString, tw } from "classnames";

type Direction = "prev" | "next";
type Size = "xs" | "sm" | "lg";

interface Props {
  className?: TTailwindString;
  currentSlide?: number;
  direction: Direction;
  disabled?: boolean;
  size?: Size;
  totalSlides?: number;
}

const styles = {
  button: (direction: Direction, size: Size, className?: TTailwindString) =>
    tw(
      "splide__arrow" as TTailwindString,
      "pointer-events-auto",
      "flex",
      "items-center",
      "justify-center",
      "rounded-full",
      "border",
      "border-solid",
      "border-primary",
      "bg-background-primary",
      "hover:bg-button-secondary-background-hover",
      "active:bg-button-secondary-background-pressed",
      "cursor-pointer",
      "transition-all",
      {
        [tw("w-8", "h-8")]: size === "xs",
        [tw("w-10", "h-10")]: size === "sm",
        [tw("w-12", "h-12", "lg:w-16", "lg:h-16")]: size === "lg",
      },
      {
        [tw("splide__arrow--prev" as TTailwindString)]: direction === "prev",
        [tw("splide__arrow--next" as TTailwindString)]: direction === "next",
      },
      className ? tw(className) : undefined
    ),
  arrowWrapper: (direction: Direction) =>
    tw("relative", "flex", "items-center", "justify-center", {
      [tw("transform rotate-180" as TTailwindString)]: direction === "prev",
    }),
  arrow: (size: Size) =>
    tw("text-primary", {
      [tw("w-2.5", "h-2.5")]: size === "xs",
      [tw("w-4", "h-4")]: size === "sm",
      [tw("w-5", "h-5", "lg:w-6", "lg:h-6")]: size === "lg",
    }),
};

const CarouselArrowBase = ({
  className,
  currentSlide = 0,
  direction,
  disabled = false,
  size = "lg",
  totalSlides = 1,
}: Props) => {
  const navigationAria = useCarouselNavigationAria({
    direction: direction === "prev" ? "previous" : "next",
    currentSlide,
    totalSlides,
    disabled,
  });

  return (
    <button
      className={tw(styles.button(direction, size, className))}
      data-mtr-as="clickModule"
      data-mtr-label={`${direction === "next" ? "forward" : "back"} arrow`}
      data-mtr-track="click"
      {...navigationAria}
    >
      <div className={tw(styles.arrowWrapper(direction))}>
        <Icon className={styles.arrow(size)} name="arrow" />
      </div>
    </button>
  );
};

export const CarouselArrow = memo(CarouselArrowBase);
